import { create } from 'zustand';
import type { Product } from '@/types/Product.types';
import useClickedProductsStore from './ClickedProductsStore';

type CurationStore = {
  selectedProducts: Product[];
  addProduct: (product: Product) => void;
  removeProduct: (productId: string | number) => void;
  isProductSelected: (productId: string | number) => boolean;
  getSelectedProducts: () => Product[];
  clearSelectedProducts: () => void;
};

const STORAGE_KEY = 'selected_products';

const safeLocalStorage = {
  get: (key: string): Product[] => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  },
  set: (key: string, value: Product[]): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  },
};

const useCurationStore = create<CurationStore>((set, get) => ({
  selectedProducts: safeLocalStorage.get(STORAGE_KEY),
  addProduct: (product: Product) => {
    set((state) => ({
      selectedProducts: [...state.selectedProducts, product],
    }));
  },
  removeProduct: (productId: string | number) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.filter(
        (p) => p.id !== productId
      ),
    }));
  },
  isProductSelected: (productId: string | number) => {
    return get().selectedProducts.some((p) => p.id === productId);
  },
  getSelectedProducts: () => {
    return get().selectedProducts;
  },
  clearSelectedProducts: () => {
    set({ selectedProducts: [] });
    safeLocalStorage.set(STORAGE_KEY, []);
    // Also clear clicked products
    useClickedProductsStore.getState().clearClickedProducts();
  },
}));

// Subscribe to changes and persist to localStorage
useCurationStore.subscribe((state) => {
  safeLocalStorage.set(STORAGE_KEY, state.selectedProducts);
});

export default useCurationStore;
